import { debounceTime, exhaustMap, map, mergeMap, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';
import { AccountService } from 'src/app/services/account.service';
import { ContractService } from 'src/app/services/contract.service';
import { EthWalletService } from 'src/app/services/eth-wallet.service';
import { Market } from '../market';
import { MainService } from 'src/app/services/main.service';
import VaultAbi from 'src/app/abi/vault';
import { Base } from '../base';
import BigNumber from 'bignumber.js';
import { combineLatest, Observable, of, timer } from 'rxjs';
import { keepUpdater, unsubscribeLater } from 'src/app/rxjs';
import { ActionCableService, sharePublishReplay, swalloError } from 'ngx-libs';
import { AccountInfo } from '../contracts/vault';

export class OffChain extends Base {
  private positionsCache = new Map<string, Observable<any[]>>();
  private balancesCache = new Map<string, Observable<BigNumber>>();
  private accountInfoCache = new Map<string, Observable<AccountInfo>>();

  constructor(
    protected contract: string,
    protected accountService: AccountService,
    protected contractService: ContractService,
    private ethWalletService: EthWalletService,
    private actionCableService: ActionCableService,
    private mainService: MainService,
    private market: Market
  ) {
    super(contract, VaultAbi, accountService, contractService);
  }

  private getMarketCache(expiry: number, strike: any, isCall: boolean) {
    return this.getMarket(expiry, strike, isCall);
  }

  private getMarket(expiry: number, strike: any, isCall: boolean) {
    return this.market.getExpiry(expiry).pipe(mergeMap((ex) => ex.strikes));
  }

  getBalanceOf(account: string) {
    account = account.toLowerCase();
    return this.mainService.markets
      .of(this.market.name)
      .accounts.of(account)
      .balance.query()
      .pipe(
        map((json) => json.balance.amount),
        tap((v) => {
          this.market.contractVault.ledger.balances[account] = new BigNumber(v).toDecimal();
        })
      );
  }

  getBalanceOfCache(account: string) {
    account = account.toLowerCase();
    if (!this.balancesCache.has(account)) {
      const received = this.actionCableService
        .received({
          channel: 'BalanceChannel',
          account
        })
        .pipe(startWith(0), debounceTime(10));
      this.balancesCache.set(
        account,
        combineLatest([received, timer(0, 60000)]).pipe(
          debounceTime(20),
          keepUpdater(),
          exhaustMap(() => this.getBalanceOf(account).pipe(swalloError(), unsubscribeLater(10000), shareReplay(1))),
          sharePublishReplay(1)
        )
      );
    }
    return this.balancesCache.get(account);
  }

  getAccountInfo(account: string) {
    account = account.toLowerCase();
    return this.getPositionsCache(account).pipe(
      mergeMap((positions) => {
        const markets = positions.map((p) => this.getMarketCache(p.expiry, p.strike, p.isCall));
        const part1 = positions.length ? [combineLatest(markets)] : [of([])];
        return combineLatest([
          ...part1,
          this.market.config.all,
          this.market.spotPricer.price,
          this.getBalanceOfCache(account)
        ]).pipe(
          take(1),
          map(([markets, c, spotPrice, balance]) => {
            return {
              positions,
              markets,
              spotPrice,
              balance,
              config: c
            };
          })
        );
      }),
      map(() => {
        return this.market.contractVault.getAccountInfo(account);
      })
    );
  }

  getAccountInfoCache(account: string) {
    account = account.toLowerCase();
    if (!this.accountInfoCache.has(account)) {
      this.accountInfoCache.set(
        account,
        combineLatest([
          this.getPositionsCache(account),
          this.market.config.all,
          this.market.spotPricer.price,
          this.getBalanceOfCache(account),
          timer(0, 60000)
        ]).pipe(
          switchMap(() => this.getAccountInfo(account)),
          shareReplay(1)
        )
      );
    }
    return this.accountInfoCache.get(account);
  }

  getPositionsCache(account: string) {
    account = account.toLowerCase();
    if (!this.positionsCache.has(account)) {
      const received = this.actionCableService
        .received({
          channel: 'PositionsChannel',
          account
        })
        .pipe(startWith(0), debounceTime(10));
      this.positionsCache.set(
        account,
        combineLatest([received, timer(0, 60000)]).pipe(
          debounceTime(20),
          keepUpdater(),
          exhaustMap(() => this.getPositions(account).pipe(swalloError(), unsubscribeLater(10000), shareReplay(1))),
          sharePublishReplay(1)
        )
      );
    }
    return this.positionsCache.get(account);
  }

  getPositions(account: string) {
    account = account.toLowerCase();
    return this.mainService.markets
      .of(this.market.name)
      .accounts.of(account)
      .positions.query()
      .pipe(
        map((json: any) => json.positions),
        tap((positions) => {
          this.market.contractVault.ledger.setPositions(account, positions);
        })
      );
  }

  getPremium(expiry: number, strike: BigNumber, isCall: boolean, amount: BigNumber) {
    return this.market.pools.pipe(
      take(1),
      mergeMap((pools) => {
        return combineLatest(
          pools.map((pool) => {
            return combineLatest([pool.reservedRate, this.getAccountInfoCache(pool.contract)]);
          })
        );
      }),
      take(1),
      map(() => {
        const data = this.market.contractVault.getPremium(expiry, strike.toDecimal(), isCall, amount.toDecimal());
        const premium = new BigNumber(data[0]).fromDecimal();
        const fee = new BigNumber(data[1]).fromDecimal();
        return {
          premium,
          fee,
          premiumWithFee: premium.plus(fee)
        };
      })
    );
  }
}
